/**
 * Pure presentation helpers for the skills feature. Kept here (no React) so
 * the band/tone/headline logic has a single home and is unit-testable.
 */

import { LEVEL_BANDS, levelLabel, type PlayerIdentity, type SkillState } from "@/shared/models";

// ---------------------------------------------------------------------------
// Band colors — the ONE palette used everywhere a skill level is shown (the
// overall dial, per-skill bars, category bars, the skill-detail header).
//
// Keyed by band NAME, so the boundaries + names stay single-sourced in
// `models.ts:LEVEL_BANDS` and this file owns only the colors. The ramp climbs
// calm→vivid: neutral slate for untrained, cool blues/teals through the
// middle, green for Sharp, and gold reserved for the top band (Crushing) so
// gold reads as mastery, not a mid-tier color.
// ---------------------------------------------------------------------------
const BAND_COLORS: Record<string, string> = {
  Learning: "#8b93a7", // slate
  Developing: "#6b8fd6", // blue
  Solid: "#3fb6ad", // teal
  Sharp: "#57c06a", // green
  Crushing: "#e8c45e", // gold: the pinnacle
};

// Muted fill for skills with no signal yet ("unmapped").
const UNMAPPED_FILL = "rgba(139,143,150,0.2)";

export interface BandMeta {
  name: string;
  color: string;
}

/** Band name + color for a 0-100 level. Name from `levelLabel`, color from
 *  the single palette above. */
export function bandMeta(level: number): BandMeta {
  const name = levelLabel(level);
  return { name, color: BAND_COLORS[name] };
}

/** Clamp a raw level to the displayable 0-100 range. */
export function clampLevel(level: number): number {
  return Math.max(0, Math.min(100, level));
}

/**
 * CSS color for a skill-level progress-bar fill — the band color (at ~80%
 * alpha), or a muted gray when the skill is unmapped. Use via inline style so
 * the band hex stays the single source of truth shared with the dial.
 */
export function barFill(level: number, unmapped = false): string {
  return unmapped ? UNMAPPED_FILL : `${bandMeta(level).color}cc`;
}

/**
 * Overall level across a set of skills — the single definition used by the
 * gauge and the category bars. Confidence-weighted mean over mapped skills
 * (signalCount > 0), matching the backend trajectory's "overall" point
 * (`skills/aggregation.py:_overall_at`). Keep the two in sync. 0 when nothing
 * is mapped.
 */
export function overallLevel(skills: SkillState[]): number {
  const mapped = skills.filter((s) => s.signalCount > 0);
  if (mapped.length === 0) return 0;
  const totalConf = mapped.reduce((sum, s) => sum + s.confidence, 0);
  const level =
    totalConf > 0
      ? mapped.reduce((sum, s) => sum + s.level * s.confidence, 0) / totalConf
      : mapped.reduce((sum, s) => sum + s.level, 0) / mapped.length;
  return Math.round(level);
}

/** The next band up + how many points to reach it, or null at the top band.
 *  Boundaries come from LEVEL_BANDS so they're not re-listed here. */
export function nextBand(level: number): { name: string; remaining: number } | null {
  const next = LEVEL_BANDS.map((b) => b.min)
    .filter((m) => m > 0 && m > level)
    .sort((a, b) => a - b)[0];
  if (next === undefined) return null;
  return { name: levelLabel(next), remaining: next - Math.round(level) };
}

export interface DeltaHeadline {
  text: string;
  cls: string;
}

/**
 * Direction + magnitude summary for a skill's charted movement over `days`.
 * `±3` deadband so noise reads as steady. Handles the `days === 1` wording.
 */
export function deltaHeadline(
  firstLevel: number,
  lastLevel: number,
  days: number,
): DeltaHeadline {
  const delta = lastLevel - firstLevel;
  const window = days === 1 ? "over the last day" : `over the last ${days} days`;
  // "pts" disambiguates the delta from a day count.
  if (delta >= 3) return { text: `Up ${delta} pts ${window}`, cls: "text-green-400" };
  if (delta <= -3)
    return { text: `Down ${Math.abs(delta)} pts ${window}`, cls: "text-red-400" };
  return { text: `Holding steady ${window}`, cls: "text-text-muted" };
}

/** Inclusive calendar-day span between two ISO timestamps (min 1). */
export function daySpan(firstIso: string, lastIso: string): number {
  return Math.max(
    1,
    Math.round((new Date(lastIso).getTime() - new Date(firstIso).getTime()) / 86_400_000),
  );
}

export interface IdentityLines {
  strength: string;
  focus: string | null;
}

/**
 * Compose the player-identity display line from the backend's structured
 * identity fields (no prose comes from the server — see
 * feedback-llm-data-structured-only). Returns null until identity is `ready`.
 */
export function composeIdentityLine(identity: PlayerIdentity): IdentityLines | null {
  if (!identity.ready || identity.topSkills.length === 0) return null;

  const names = identity.topSkills.map((s) => s.name);
  const joined =
    names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  const strength = `Sharpest at ${joined}`;
  const focus = identity.focusSkill ? `Working on ${identity.focusSkill.name}` : null;

  return { strength, focus };
}
